import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';

const CATEGORIES = [
  { key: 'getting-started', label: 'Getting Started', icon: 'sparkles' },
  { key: 'family-tree', label: 'Family Tree', icon: 'family-tree' },
  { key: 'memories', label: 'Memories', icon: 'image' },
  { key: 'invitations', label: 'Invitations', icon: 'mail' },
  { key: 'settings', label: 'Settings', icon: 'settings' },
  { key: 'troubleshooting', label: 'Troubleshooting', icon: 'settings' },
];

const ARTICLES = {
  'getting-started': [
    { title: 'How to sign up', content: 'Visit the Mien Family app and create an account using your email and password. You\'ll receive a confirmation email to verify your account.' },
    { title: 'Add your first family member', content: 'After signing in, click "Add Family Member" from the dashboard. Fill in their details including name, birth date, and how you\'re related.' },
    { title: 'Invite your relatives', content: 'Use the "Invite Family" button to send invitations via email or SMS. Your relatives will receive a link to join your family tree.' },
  ],
  'family-tree': [
    { title: 'Navigating the family tree', content: 'Use the three view modes — Descendant, Pedigree, and Fan Chart — to explore your family tree. Zoom in/out using the controls in the header.' },
    { title: 'Finding relatives', content: 'Use the search box to find a specific family member by name. Click on any node to see their profile and kinship term.' },
    { title: 'Understanding kinship terms', content: 'Mien culture has specific terms for each family relationship. Hover over nodes in the tree to see the kinship term relative to you.' },
  ],
  'memories': [
    { title: 'Adding a memory', content: 'Click "Add Memory" and choose the type — Photo, Story, Audio, or Video. Upload your file or write your story, tag people, and save.' },
    { title: 'Organizing memories', content: 'Use tags to categorize memories. Filter by type (Photo, Story, etc.) or search by title and tags.' },
  ],
  'invitations': [
    { title: 'Sending an invitation', content: 'Navigate to Invitations and click "Invite Family". Choose email or SMS, enter their contact info, and select what permission level they should have.' },
    { title: 'Managing invitations', content: 'View your sent and received invitations in the Invitations page. Resend or cancel pending invitations, and accept or reject received ones.' },
  ],
  'settings': [
    { title: 'Customizing your profile', content: 'Go to Settings → Account to update your name, email, and phone number. You can also change your password there.' },
    { title: 'Notification preferences', content: 'In Settings → Account, toggle notifications for memories, invitations, events, and kinship updates.' },
    { title: 'Privacy settings', content: 'Control what profile information others can see — birth date, contact info, and location — in Settings → Account → Privacy.' },
  ],
  'troubleshooting': [
    { title: 'Can\'t log in', content: 'Make sure you\'re using the correct email and password. Try resetting your password using the "Forgot password" link on the login page.' },
    { title: 'Invitation not received', content: 'Check your spam/junk folder. If the invitation still hasn\'t arrived, ask the sender to resend it from the Invitations page.' },
    { title: 'Tree not loading', content: 'Try refreshing the page. If the issue persists, check your internet connection. Large trees may take a few seconds to load.' },
  ],
};

function HelpPage() {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [search, setSearch] = useState('');
  const [expandedArticle, setExpandedArticle] = useState(null);

  const filteredArticles = search
    ? Object.values(ARTICLES).flat().filter((a) =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.content.toLowerCase().includes(search.toLowerCase())
      )
    : ARTICLES[activeCategory] || [];

  return (
    <div className="">

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search help articles..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className="md:w-56 shrink-0">
            <nav className="space-y-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => { setActiveCategory(cat.key); setSearch(''); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === cat.key && !search ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <AppIcon name={cat.icon} size={16} /> {cat.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Articles */}
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {search ? `Search results (${filteredArticles.length})` : CATEGORIES.find((c) => c.key === activeCategory)?.label}
            </h2>
            <div className="space-y-3">
              {filteredArticles.map((article, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setExpandedArticle(expandedArticle === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900">{article.title}</span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedArticle === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedArticle === i && (
                    <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
                      {article.content}
                    </div>
                  )}
                </div>
              ))}
              {filteredArticles.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                  <AppIcon name="search" size={36} />
                  <p className="mt-3 text-gray-500">No articles found. Try a different search.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-12 bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900">Still need help?</h3>
          <p className="mt-2 text-sm text-gray-500">Can't find what you're looking for? Get in touch with us.</p>
          <div className="mt-4 flex gap-3 justify-center">
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">Contact Support</button>
            <a href="/about" className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">About Mien Family</a>
          </div>
        </div>
      </main>
    </div>
  );
}

export default HelpPage;